import Component from '@glimmer/component';
import { and, not, or, eq } from 'ember-truth-helpers';
import can from 'ember-can/helpers/can';
import { variation } from 'ember-launch-darkly';
import { t } from 'ember-intl';
import {
  HdsPageHeader,
  HdsAlert,
  HdsTooltipButton,
  HdsIcon,
  HdsBadge,
  HdsTextBody,
  HdsCopyButton,
  HdsLinkStandalone,
} from '@hashicorp/design-system-components/components';
import {
  CoreLayoutMain,
  BreadcrumbsContainer,
  FlexGrid,
  DefinitionList,
  Flex,
  Time,
} from 'core/components';
import { boxPadding } from 'core/modifiers';
import { optionForSourceState } from 'core/helpers';
import DefaultRoleAdmin from 'hcp/components/sso/banner/default-role-admin';
import Actions from 'hcp/components/page/organizations/actions';

/**
 *
 * `PageOrganizationsDetail` shows details about and organization and notifies
 * a user if their SSO isn't set up.
 *
 *
 * ```
 * <Page::Organizations::Detail
 *   @model={{this.model}}
 * />
 * ```
 *
 * @class PageOrganizationsDetail
 *
 */
export default class PageOrganizationsDetail extends Component {
  /**
   * The expected model is an object consisting of:
   * @param `model.owner`: the user principal who owns the org
   * @param `model.organization`: the organization record of the organization
   *     in question.
   * @param `model.hasSSO`: a boolean indicating if the org has SSO configured.
   * @param `model.ssoType`: the type of SSO configured for the org.
   * @param `model.defaultRole`: the default role for the org.
   * @param `model.businessInfo`: the business information for the org.
   * @argument model
   * @type {object}
   */

  get isBusinessInfoAvailable() {
    // Checks for a required business info field
    return this.args.model?.businessInfo?.businessName;
  }
  <template>
    {{#let
      (and
        (not @model.hasSSO) (or (can "CRUD sso") (can "CRUD auth-connection"))
      )
      (and
        (eq @model.defaultRole "roles/admin")
        (variation "hcp-identity-sso-default-role")
      )
      as |ssoNotConfigured isSsoDefaultRoleAdmin|
    }}
      {{#if isSsoDefaultRoleAdmin}}
        <DefaultRoleAdmin @ssoType={{@model.ssoType}} />
      {{/if}}

      <CoreLayoutMain>
        <:header>
          <HdsPageHeader as |PH|>
            <PH.Breadcrumb>
              <BreadcrumbsContainer />
            </PH.Breadcrumb>
            <PH.Title>
              {{@model.organization.name}}
            </PH.Title>
            <PH.IconTile @icon="org" />
            <PH.Actions>
              <Actions
                @next="cloud.orgs.detail.settings"
                @organization={{@model.organization}}
                @compact={{false}}
                @canRename={{can "rename organization"}}
              />
            </PH.Actions>
          </HdsPageHeader>
        </:header>

        <:alert>
          {{#if ssoNotConfigured}}
            <HdsAlert
              @type="inline"
              @color="neutral"
              data-test-sso-banner
              as |A|
            >
              <A.Title>
                {{t "components.page.organizations.settings.sso-banner.header"}}
              </A.Title>
              <A.Description>
                {{t "components.page.organizations.settings.sso-banner.body"}}
              </A.Description>
              <A.LinkStandalone
                @icon="arrow-right"
                @iconPosition="trailing"
                @model={{@model.organization.id}}
                @route="cloud.orgs.detail.settings.sso.select-method"
                @text={{t
                  "components.page.organizations.settings.sso-banner.footer"
                }}
                data-test-setup-sso
              />
            </HdsAlert>
          {{/if}}
        </:alert>
        <:body>
          <FlexGrid as |G|>
            <G.Item @xs="12" @sm="12" @md="6" @lg="6">
              <DefinitionList {{boxPadding "md 0 xl 0"}} as |DL|>
                {{#if this.isBusinessInfoAvailable}}
                  <DL.Key data-test-business-info-key>
                    <Flex @direction="row" @gap="xs" @alignItems="center">
                      {{t
                        "components.page.organizations.settings.business-info.key"
                      }}
                      <HdsTooltipButton
                        @text={{t
                          "components.page.organizations.settings.business-info.tooltip-text"
                        }}
                      >
                        <HdsIcon @name="info" />
                      </HdsTooltipButton>
                    </Flex>
                  </DL.Key>
                  {{#let @model.businessInfo as |business|}}
                    <DL.Value data-test-business-info-value>
                      <Flex @direction="column" @gap="xs">
                        <div
                          class="hds-foreground-strong"
                          data-test-business-name-value
                        >{{business.businessName}}</div>
                        <div
                          data-test-business-addressLine1-value
                        >{{business.addressLine1}}</div>
                        {{#if business.addressLine2}}
                          <div>{{business.addressLine2}}</div>
                        {{/if}}
                        <div>
                          <span
                            data-test-business-city-value
                          >{{business.city}}</span>{{#if business.state}}<span>,
                              {{business.state}}</span>{{/if}}
                        </div>
                        {{#if business.postalCode}}
                          <div>{{business.postalCode}}</div>
                        {{/if}}
                        <div
                          data-test-business-country-code-value
                        >{{business.countryCode}}</div>
                      </Flex>
                    </DL.Value>
                  {{/let}}
                {{/if}}
                <DL.Key>
                  {{t "components.page.organizations.settings.status"}}
                </DL.Key>
                <DL.Value>
                  {{#let
                    (optionForSourceState
                      "ORGANIZATION" @model.organization.state
                    )
                    as |option|
                  }}
                    <HdsBadge
                      @color={{option.color}}
                      @icon={{option.icon}}
                      @isIconOnly={{option.isIconOnly}}
                      @size={{option.size}}
                      @text={{t option.text}}
                      @type={{option.type}}
                    />
                  {{/let}}
                </DL.Value>
                <DL.Key>
                  {{t "components.page.organizations.settings.created"}}
                </DL.Key>
                <DL.Value>
                  <Time @date={{@model.organization.createdAt}} />
                </DL.Value>
                <DL.Key>
                  {{t "components.page.organizations.settings.name"}}
                </DL.Key>
                <DL.Value>
                  {{@model.organization.name}}
                </DL.Value>
                <DL.Key>
                  {{t "components.page.organizations.settings.id"}}
                </DL.Key>
                <DL.Value>
                  <Flex @alignItems="center" @direction="row" @gap="xs">
                    <HdsTextBody>{{@model.organization.id}}</HdsTextBody>
                    <HdsCopyButton
                      @isIconOnly={{true}}
                      @size="small"
                      @text={{@model.organization.id}}
                      @textToCopy={{@model.organization.id}}
                    />
                  </Flex>
                </DL.Value>
                {{#if @model.owner}}
                  <DL.Key>
                    {{t "components.page.organizations.settings.owner"}}
                  </DL.Key>
                  <DL.Value>
                    <Flex @alignItems="center" @direction="row" @gap="xs">
                      <HdsTextBody>{{@model.owner.email}}</HdsTextBody>
                      <HdsCopyButton
                        @isIconOnly={{true}}
                        @size="small"
                        @text={{@model.owner.email}}
                        @textToCopy={{@model.owner.email}}
                      />
                    </Flex>
                  </DL.Value>
                {{/if}}
                {{#if (or (can "CRUD sso") (can "CRUD auth-connection"))}}
                  <DL.Key data-test-sso-enabled-key>
                    {{t "components.page.organizations.settings.sso"}}
                  </DL.Key>
                  <DL.Value class="orgSettings__ssoEnabled">
                    {{#if ssoNotConfigured}}
                      <Flex @direction="column" @gap="md">
                        <span>
                          {{t
                            "components.page.organizations.settings.sso-not-enabled"
                          }}
                        </span>
                        <HdsLinkStandalone
                          @icon="arrow-right"
                          @iconPosition="trailing"
                          @model={{@model.organization.id}}
                          @route="cloud.orgs.detail.settings.sso.select-method"
                          @text={{t
                            "components.page.organizations.settings.sso-setup"
                          }}
                          data-test-sso-create-link
                        />
                      </Flex>
                    {{else if @model.hasSSO}}
                      {{t "components.page.organizations.settings.sso-enabled"}}
                    {{else}}
                      {{t
                        "components.page.organizations.settings.sso-not-enabled"
                      }}
                    {{/if}}
                  </DL.Value>
                {{/if}}
              </DefinitionList>
            </G.Item>
          </FlexGrid>
        </:body>
      </CoreLayoutMain>
    {{/let}}
  </template>
}
